import React, { useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import Icon from './shared/Icon';
import { isImageSrc } from '../utils/imageSource';
import useUnreadNotificationCount from '../hooks/useUnreadNotificationCount';
import { useBottomTabBarVisible } from '../utils/bottomTabBar';

/**
 * BottomTabBar — the phone navigation for signed-in users (2026-10-01).
 * Replaces the top nav below `md` (Navigation steps aside on the same rule,
 * src/utils/bottomTabBar.js). Desktop never renders it.
 *
 * Reserves its own height: while it shows, <html> carries `has-tabbar`, which
 * sets --app-tabbar-h (and zeroes --app-topnav-h) on phones in index.css.
 * Anything fixed to the bottom of the screen offsets by --app-tabbar-h, and
 * full-height layouts subtract both variables.
 *
 * Active tab in harbour blue (blue = "you are here"); a coral dot marks a tab
 * with something new to try; the unread notification count badges You, since
 * there is no top bar to hold a bell on phones. You shows your profile photo
 * (Instagram's pattern) — a photo can't turn blue, so its active state is a
 * harbour ring; initials on a tint when there's no photo.
 */
const TABS = [
  { key: 'home', path: '/', icon: 'home', match: (p) => p === '/' },
  { key: 'discover', path: '/social-feed', icon: 'compass', match: (p) => p.startsWith('/social-feed') || p.startsWith('/whats-on') || p.startsWith('/search-results') || p.startsWith('/destinations') || p.startsWith('/destination/') },
  { key: 'planner', path: '/activity-planner', icon: 'calendar', match: (p) => p.startsWith('/activity-planner') || p.startsWith('/trip/') || p.startsWith('/rate-day') || p.startsWith('/day-out') },
  { key: 'saved', path: '/my-saved-items', icon: 'bookmark', match: (p) => p.startsWith('/my-saved-items') || p.startsWith('/collections') },
  // You opens your own profile (its owner bar holds the bell and the /you menu)
  { key: 'you', path: '/profile', icon: 'user', match: (p) => p === '/you' || p === '/profile' || p.startsWith('/settings') || p.startsWith('/messages') || p.startsWith('/creator-studio') || p.startsWith('/advertise') },
];

function BottomTabBar() {
  const { t } = useTranslation();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { hasMilestone, isLoading: onboardingLoading } = useOnboarding();
  const visible = useBottomTabBarVisible();
  // Only subscribe while the bar shows (not on desktop, not in the planner)
  const unreadCount = useUnreadNotificationCount(visible ? currentUser : null);

  // The one owner of `has-tabbar`: set while shown, cleared on hide/unmount
  // so no page keeps reserving space for a bar that isn't there. Layout
  // effect, so the CSS variables flip in the same frame the bar appears.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (visible) root.classList.add('has-tabbar');
    else root.classList.remove('has-tabbar');
    return () => root.classList.remove('has-tabbar');
  }, [visible]);

  if (!visible) return null;

  // A dot means "you haven't been here yet" and clears on the first visit
  // (each page records its visit milestone). Nothing until onboarding state
  // has loaded: every milestone starts false, so established users saw all
  // three dots flash on each cold load.
  const showNew = onboardingLoading ? {} : {
    discover: !hasMilestone('visitedDiscover'),
    planner: !hasMilestone('visitedPlanner'),
    saved: !hasMilestone('visitedSavedItems'),
  };
  const label = (key) => (key === 'you' ? t('nav.tabBar.you') : t(`nav.items.${key}.label`));

  return (
    <nav
      aria-label={t('nav.tabBar.ariaLabel')}
      className="md:hidden fixed inset-x-0 bottom-0 z-sticky bg-surface/95 backdrop-blur border-t border-border pb-[env(safe-area-inset-bottom)]"
    >
      <ul className="grid grid-cols-5 h-14">
        {TABS.map((tab) => {
          const active = tab.match(location.pathname);
          const badge = tab.key === 'you' && unreadCount > 0
            ? (unreadCount > 9 ? t('nav.notifications.countCap') : String(unreadCount))
            : null;
          return (
            <li key={tab.key} className="min-w-0">
              <Link
                to={tab.path}
                aria-current={active ? 'page' : undefined}
                aria-label={badge
                  ? t('nav.tabBar.withUnread', { label: label(tab.key), count: unreadCount })
                  : undefined}
                className={`h-full flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-light ${
                  active ? 'text-primary' : 'text-content-secondary active:text-content-primary'
                }`}
              >
                <span className="relative">
                  {tab.key === 'you' ? (
                    <span className={`block w-6 h-6 rounded-full overflow-hidden ${active ? 'ring-2 ring-primary ring-offset-1 ring-offset-surface' : 'ring-1 ring-border'}`}>
                      {isImageSrc(currentUser?.profilePicture) ? (
                        <img src={currentUser.profilePicture} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="w-full h-full bg-primary-lighter text-primary-dark flex items-center justify-center text-[11px] font-bold" aria-hidden="true">
                          {(currentUser?.name || '').trim().charAt(0).toUpperCase() || t('nav.userMenu.avatarFallbackInitial')}
                        </span>
                      )}
                    </span>
                  ) : (
                    <Icon name={tab.icon} className="w-6 h-6" />
                  )}
                  {showNew[tab.key] && !active && (
                    <span className="absolute -top-0.5 -right-1 w-2 h-2 rounded-full bg-coral" aria-hidden="true" />
                  )}
                  {badge && (
                    <span className="absolute -top-1.5 left-3.5 min-w-[18px] h-[18px] px-1 rounded-full bg-error text-content-inverse text-[10px] font-bold leading-[18px] text-center tabular-nums" aria-hidden="true">
                      {badge}
                    </span>
                  )}
                </span>
                <span className="truncate max-w-full px-1">{label(tab.key)}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export default BottomTabBar;
